import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { CopyEmail } from "./CopyEmail";

const CAREER_EMAIL = "mail@arihantinfracon.com";

export const Footer = () => {
  const [isCareerModalOpen, setIsCareerModalOpen] = useState(false);

  useEffect(() => {
    if (!isCareerModalOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsCareerModalOpen(false);
      }
    };

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isCareerModalOpen]);

  return (
    <footer className="bg-neutral-950 text-white pt-24 pb-8 px-6 md:px-12 lg:px-24 border-t border-neutral-900">
      <div className="max-w-7xl mx-auto">
        {/* Top Section: Typography & Contact */}
        <div className="flex flex-col lg:flex-row justify-between items-start gap-12 lg:gap-24 mb-24">
          <div className="flex-1">
            <img
              src="/logo-h.png"
              alt="Arihant Infracon"
              className="h-16 md:h-20 lg:h-24 w-auto mb-6"
            />
            <p className="text-neutral-400 text-lg max-w-md font-light">
              Engineering the spaces of tomorrow with precision, passion, and
              perfection.
            </p>
          </div>

          <div className="flex flex-col items-start lg:items-end">
            <span className="text-primary uppercase tracking-widest text-sm font-bold mb-4">
              Get in Touch
            </span>
            <CopyEmail
              email="mail@arihantinfracon.com"
              className="font-serif text-2xl md:text-3xl lg:text-4xl text-white hover:text-white/80 transition-colors relative group">
              mail@arihantinfracon.com
              <span className="absolute left-0 bottom-0 w-0 h-0.5 bg-primary transition-all duration-300 group-hover:w-full" />
            </CopyEmail>
          </div>
        </div>

        {/* Middle Section: Links Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-12 mb-20">
          {[
            {
              title: "Sitemap",
              links: [
                { name: "Home", path: "/" },
                { name: "About Us", path: "/about" },
                { name: "Careers", path: "/careers" },
                { name: "Contact", path: "/contact" },
              ],
            },
            {
              title: "Expertise",
              links: [
                { name: "Luxury Interiors", path: "/services" },
                { name: "Civil Contracting", path: "/services" },
                { name: "MEP Services", path: "/services" },
                { name: "Design Build", path: "/services" },
              ],
            },
            {
              title: "Policies",
              links: [
                { name: "Environment Policy", path: "/environment-policy" },
                { name: "HSE Policy", path: "/hse-policy" },
                { name: "Quality Policy", path: "/quality-policy" },
              ],
            },
            {
              title: "Legal",
              links: [
                { name: "Privacy Policy", path: "#" },
                { name: "Terms of Service", path: "#" },
                { name: "Disclaimer", path: "#" },
              ],
            },
          ].map((column, index) => (
            <div key={index}>
              <h4 className="text-lg font-bold mb-6 font-serif">
                {column.title}
              </h4>
              <ul className="space-y-4">
                {column.links.map((link, lIndex) => (
                  <li key={lIndex}>
                    {link.name === "Careers" ? (
                      <button
                        type="button"
                        onClick={() => setIsCareerModalOpen(true)}
                        className="text-neutral-400 hover:text-primary transition-colors flex items-center gap-2 group">
                        {link.name}
                        <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity transform translate-y-1 group-hover:translate-y-0" />
                      </button>
                    ) : (
                      <Link
                        to={link.path}
                        className="text-neutral-400 hover:text-primary transition-colors flex items-center gap-2 group">
                        {link.name}
                        <ArrowUpRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity transform translate-y-1 group-hover:translate-y-0" />
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Section */}
        <div className="pt-8 border-t border-primary/30 flex flex-col md:flex-row justify-between items-center gap-4 text-sm text-neutral-500">
          <p>
            &copy; {new Date().getFullYear()} Arihant Infracon. All rights
            reserved.
          </p>
          <p className="flex items-center gap-2">
            Designed by{" "}
            <a
              href="https://www.codeangle.in"
              target="_blank"
              rel="noopener noreferrer">
              CodeAngle Technologies Pvt Ltd
            </a>
          </p>
        </div>
      </div>

      <AnimatePresence>
        {isCareerModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm p-6 flex items-center justify-center"
            onClick={() => setIsCareerModalOpen(false)}>
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.98 }}
              transition={{ duration: 0.2 }}
              onClick={(event) => event.stopPropagation()}
              className="w-full max-w-xl border border-white/20 bg-neutral-900 text-white p-8 rounded-2xl shadow-[0_30px_80px_rgba(0,0,0,0.45)]">
              <p className="text-primary text-xs font-semibold tracking-[0.35em] uppercase mb-4">
                Careers
              </p>
              <h3 className="font-serif text-3xl mb-4">
                Join Arihant Infracon
              </h3>
              <p className="text-neutral-300 leading-relaxed mb-6">
                To apply, please send your resume on the email below.
              </p>
              <CopyEmail
                email={CAREER_EMAIL}
                className="font-serif text-xl md:text-2xl text-white hover:text-white/80 transition-colors">
                {CAREER_EMAIL}
              </CopyEmail>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </footer>
  );
};
